import { flow, getRoot, Instance, types } from 'mobx-state-tree';
import { TUTORIAL_DEFAULT_VALUES } from './tutorial.constants';
import { IArrow, IHole, ITutorialCard } from '@photosi/rn-components/tutorial/Tutorial.types';
import { TUTORIALS_DESKTOP, TUTORIALS_MOBILE } from '@photosi/rn-components/tutorial/Tutorial.constants';
import { IRootStore } from '../store';

export type TutorialInstance = Instance<typeof Tutorial>;

export const Tutorial = types
  .model({
    tutorialVisible: types.boolean,
  })
  .volatile<{
    firstProductArea: IHole;
    sliderZoomArea: IHole;
    toolbarArea: IHole;
    toolbarAreaMobile: IHole;
    activeStep: {
      tutorialId: string;
      stepId: string;
      targetArea: string;
      tutorialCard: ITutorialCard;
      arrow: IArrow;
      onPressNext: string;
    };
    isTutorialOn: boolean;
    firstProductRef: any;
    showTutorialDesktop: boolean;
    withBackground: boolean;
  }>(self => ({
    firstProductArea: TUTORIAL_DEFAULT_VALUES.targetArea,
    sliderZoomArea: TUTORIAL_DEFAULT_VALUES.targetArea,
    toolbarArea: TUTORIAL_DEFAULT_VALUES.targetArea,
    toolbarAreaMobile: TUTORIAL_DEFAULT_VALUES.targetArea,
    activeStep: {
      tutorialId: 'tutorial-1',
      stepId: 'step-1',
      targetArea: 'firstProductArea',
      arrow: TUTORIAL_DEFAULT_VALUES.arrow,
      tutorialCard: TUTORIAL_DEFAULT_VALUES.tutorialCard,
      onPressNext: 'selectFirstProduct',
    },
    isTutorialOn: false,
    firstProductRef: null as any,
    showTutorialDesktop: TUTORIAL_DEFAULT_VALUES.showTutorialDesktop,
    withBackground: true,
  }))
  .views(self => ({
    get root(): IRootStore {
      return getRoot(self);
    },
  }))
  .actions(self => {
    return {
      closeTutorial: () => {
        self.tutorialVisible = false;
      },
      setTutorialArea: ({ key, value }: { key: string; value: IHole }) => {
        if (self[key]) self[key] = value;
      },
      setFirstProductRef(ref: any) {
        self.firstProductRef = ref;
      },
    };
  })
  .actions(self => {
    return {
      measureFirstProduct() {
        return new Promise((resolve, reject) => {
          self.firstProductRef?.current?.measure((x, y, width, height, pageX, pageY) => {
            self.setTutorialArea({
              key: 'firstProductArea',
              value: {
                width,
                height,
                x: pageX,
                y: pageY,
                borderRadius: 24,
              },
            });

            resolve(true);
          });
        });
      },
      onBeforeStep: (key: string) => {
        switch (key) {
          case 'selectFirstProduct':
            self.root.project.switchToGroup({
              groupId: self.root.project.activeGroup!.id,
              scrollTo: 'top',
            });
            break;
          case 'closeTutorial':
            break;
        }
      },
    };
  })
  .actions(self => {
    return {
      showTutorial: flow(function* showTutorial({
        tutorialId,
        stepId,
        isDesktop,
      }: {
        tutorialId: string;
        stepId: string;
        isDesktop: boolean;
      }) {
        self.isTutorialOn = true;
        self.showTutorialDesktop = isDesktop;

        const TUTORIALS = isDesktop ? TUTORIALS_DESKTOP : TUTORIALS_MOBILE;
        const stepToShow = TUTORIALS[tutorialId].find(step => step.stepId === stepId);

        self.withBackground = stepToShow.withBackground;
        self.onBeforeStep(stepToShow.onPressNext);
        // get Area
        if (self.firstProductRef?.current) yield self.measureFirstProduct();

        if (self[stepToShow.targetArea]) {
          self.activeStep = {
            tutorialId: tutorialId,
            stepId: stepId,
            targetArea: stepToShow.targetArea,
            arrow: stepToShow.arrow,
            tutorialCard: stepToShow.tutorialCard,
            onPressNext: stepToShow.onPressNext,
          };

          self.tutorialVisible = true;
        }
      }),
      updateTutorial: flow(function* updateTutorial() {
        if (self.firstProductRef?.current) yield self.measureFirstProduct();
      }),
    };
  })
  .actions(self => {
    return {
      onNextHandler: (key: string) => {
        self.tutorialVisible = false;
        switch (key) {
          case 'selectFirstProduct':
            self.root.project.activeGroup?.products[0]?.setIsSelected(true);
            break;
          case 'closeTutorial':
            self.isTutorialOn = false;
        }
        setTimeout(async () => {
          await self.updateTutorial();
          const tutorialId = self.activeStep.tutorialId;

          const TUTORIALS = self.showTutorialDesktop ? TUTORIALS_DESKTOP : TUTORIALS_MOBILE;
          const activeTutorial = TUTORIALS[tutorialId];
          const actualIndex = activeTutorial.findIndex(step => step.stepId === self.activeStep.stepId);

          if (!!activeTutorial[actualIndex + 1])
            self.showTutorial({
              tutorialId: tutorialId,
              stepId: activeTutorial[actualIndex + 1].stepId,
              isDesktop: self.showTutorialDesktop,
            });
        }, 1000);
      },
    };
  });
